import { Link } from 'react-router'

import { errorMessage } from '@/api/client'
import { usePlans } from '@/api/queries'
import { upgradeHint, upgradeSentence } from '@/lib/billing'

/**
 * The message for an API error, plus -- when the error is a refused quota -- the plan that lifts
 * it and a link to the Plan page (stripe.md §11, "upgrade prompts at the point of refusal").
 *
 * Use it in place of `errorMessage(error)` anywhere a create, join, invite or reactivate can hit a
 * cap: zones, connections, channels, members. Any other error renders exactly as before.
 */
export function ApiErrorText({ error }: { error: unknown }) {
  const plans = usePlans()
  const hint = upgradeHint(error)
  if (hint === null) return <>{errorMessage(error)}</>

  const planName = (code: string) => plans.data?.find((plan) => plan.code === code)?.name ?? code
  return (
    <span className="flex flex-col gap-1" data-testid="upgrade-prompt" data-quota={hint.code}>
      <span>{errorMessage(error)}</span>
      <span>
        {upgradeSentence(hint, planName)}{' '}
        <Link to="/app/plan" className="font-medium text-primary underline-offset-4 hover:underline">
          {hint.upgradePlan === null ? 'See the plans' : `Upgrade to ${planName(hint.upgradePlan)}`}
        </Link>
      </span>
    </span>
  )
}
